import { RefObject, useEffect, useState } from 'react';

/**
 * Tracks the rendered height of an element that appears, disappears, or reflows after mount.
 *
 * Used to reserve space below the marketing layouts' `fixed` header for the regional-offer bar,
 * which only exists once the country lookup resolves and which wraps to two rows on narrow
 * screens. The hardcoded header spacers are sized for the NavBar alone, so without this the bar
 * pushes the first heading underneath the header.
 *
 * Deliberately does NOT rely on `ResizeObserver`: it is unavailable or inert in some environments
 * (including headless Chrome), and a silent 0 here means occluded content. A MutationObserver on
 * the subtree catches the bar mounting and unmounting, and a resize listener catches the row wrap.
 */
export default function useMeasuredHeight(ref: RefObject<HTMLElement>): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = () => setHeight(element.getBoundingClientRect().height);
    measure();

    const observer = new MutationObserver(measure);
    observer.observe(element, { childList: true, subtree: true, attributes: true });
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ref]);

  return height;
}
